import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Select from 'react-select';
import toast from 'react-hot-toast';
import { Sparkles, Save, CheckCircle2, AlertTriangle, RefreshCw, Zap } from 'lucide-react';
import { configuracionAPI, aiAPI } from '../services/api';
import { SecretInput, selectStyles } from '../utils/adminShared';
import { Button } from './ui';

// Campos que se guardan al pulsar "Guardar" en cada tab. Las API keys
// (SecretInput) se guardan inline cuando el usuario confirma su edicion.
const CAMPOS_GENERAL = ['ia_activada', 'ia_proveedor_default'];
const CAMPOS_GEMINI  = ['ia_modelo', 'usar_gemini_fallback'];
const CAMPOS_DEEPSEEK = ['deepseek_modelo', 'usar_deepseek_fallback'];
const CAMPOS_CLAUDE  = ['claude_modelo', 'usar_claude_fallback'];

// Listas canonicas de modelos vigentes por proveedor. Sirven para (a) elegir
// el default cuando no hay nada guardado y (b) detectar si el valor actual
// quedo obsoleto — los obsoletos siguen disponibles en el select con marca
// "(obsoleto)" para no romper configs viejas ni ocultar lo que hay guardado.
const MODELOS_VIGENTES = {
    ia_modelo: [
        'gemini-3.8-flash', 'gemini-3.7-flash', 'gemini-3.6-flash',
        'gemini-3.5-flash', 'gemini-3.5-flash-lite'
    ],
    deepseek_modelo: ['deepseek-chat', 'deepseek-reasoner'],
    claude_modelo: ['claude-haiku-4-5-20251001', 'claude-sonnet-5']
};
const DEFAULT_MODELO = {
    ia_modelo: 'gemini-3.8-flash',
    deepseek_modelo: 'deepseek-chat',
    claude_modelo: 'claude-haiku-4-5-20251001'
};

const PROVEEDORES_CHAT = [
    { key: 'gemini', label: 'Gemini' },
    { key: 'deepseek', label: 'DeepSeek' },
    { key: 'claude', label: 'Claude' }
];

const TAB_KEYS = ['general', 'gemini', 'deepseek', 'claude'];
const TAB_TITLES = {
    general:  'Ajustes generales',
    gemini:   'Gemini',
    deepseek: 'DeepSeek',
    claude:   'Claude (Anthropic)'
};

const humanStatusLabel = (status) => {
    switch (status) {
        case 'ok':            return 'OK';
        case 'rate_limited':  return 'Rate limit';
        case 'invalid_key':   return 'API key inválida';
        case 'no_balance':    return 'Sin saldo';
        case 'model_error':   return 'Modelo inválido';
        case 'network':       return 'Red / timeout';
        case 'half_open':     return 'Probando';
        default:              return status || 'Error';
    }
};

const ConfiguracionIAPanel = () => {
    const { tab: tabParam } = useParams();
    // tab param invalido (ej: /configuracion-ia/jev tras retirar Jev) → 404.
    // Si no hay param (ruta base /configuracion-ia) usa 'general'.
    const tabInvalido = tabParam !== undefined && !TAB_KEYS.includes(tabParam);
    const tab = tabParam && TAB_KEYS.includes(tabParam) ? tabParam : 'general';
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({});
    const [local, setLocal] = useState({});
    const [aiStatus, setAiStatus] = useState(null);
    const [busyProvider, setBusyProvider] = useState(null);

    const cargarStatus = useCallback(async () => {
        try {
            const res = await aiAPI.getStatus();
            setAiStatus(res.data);
        } catch { /* silent */ }
    }, []);

    useEffect(() => {
        cargar();
        cargarStatus();
    }, [cargarStatus]);

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await configuracionAPI.getAll();
            const data = res.data || {};
            setConfig(data);

            // Detectar modelos obsoletos: se mantiene el valor guardado en el
            // select (para no cambiar la config sin permiso) y se avisa por toast.
            const obsoletos = [];
            const chequear = (clave) => {
                const guardado = data[clave];
                if (guardado && !MODELOS_VIGENTES[clave].includes(guardado)) {
                    obsoletos.push({ clave, guardado });
                }
                return guardado || DEFAULT_MODELO[clave];
            };

            setLocal({
                ia_activada: data.ia_activada ?? '1',
                ia_proveedor_default: data.ia_proveedor_default || 'gemini',
                ia_modelo: chequear('ia_modelo'),
                usar_gemini_fallback: data.usar_gemini_fallback ?? '1',
                deepseek_modelo: chequear('deepseek_modelo'),
                usar_deepseek_fallback: data.usar_deepseek_fallback || '0',
                claude_modelo: chequear('claude_modelo'),
                usar_claude_fallback: data.usar_claude_fallback || '0'
            });

            if (obsoletos.length) {
                obsoletos.forEach(({ clave, guardado }) => {
                    const prov = clave === 'ia_modelo' ? 'Gemini'
                        : clave === 'deepseek_modelo' ? 'DeepSeek'
                        : 'Claude';
                    toast(
                        `El modelo ${prov} guardado (${guardado}) está marcado como obsoleto. Se sugiere cambiar a ${DEFAULT_MODELO[clave]}.`,
                        { icon: '⚠️', duration: 8000 }
                    );
                });
            }
        } catch (err) {
            console.error('Error al cargar configuración IA:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleImmediateSave = async (clave, valor) => {
        try {
            await configuracionAPI.update(clave, valor);
            setConfig(prev => ({ ...prev, [clave]: valor }));
            toast.success('API Key guardada');
        } catch (err) {
            toast.error('Error al actualizar: ' + err.message);
        }
    };

    const handleLocalChange = (clave, valor) => {
        setLocal(prev => ({ ...prev, [clave]: valor }));
    };

    // Campos que guarda la tab activa
    const camposTab = useMemo(() => {
        switch (tab) {
            case 'general':  return CAMPOS_GENERAL;
            case 'gemini':   return CAMPOS_GEMINI;
            case 'deepseek': return CAMPOS_DEEPSEEK;
            case 'claude':   return CAMPOS_CLAUDE;
            default: return [];
        }
    }, [tab]);

    const hayCambios = camposTab.some(k => (config[k] ?? '') !== (local[k] ?? ''));

    const guardar = async () => {
        if (!hayCambios) return;
        setSaving(true);
        try {
            const cambios = camposTab.filter(k => (config[k] ?? '') !== (local[k] ?? ''));
            await Promise.all(cambios.map(k => configuracionAPI.update(k, local[k])));
            setConfig(prev => ({ ...prev, ...Object.fromEntries(cambios.map(k => [k, local[k]])) }));
            toast.success('Cambios guardados');
        } catch (err) {
            toast.error('Error al guardar: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const probarProveedor = async (provider) => {
        setBusyProvider(provider);
        try {
            const res = await aiAPI.testProvider(provider);
            toast.success(res.message);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusyProvider(null);
            cargarStatus();
        }
    };

    const reactivarProveedor = async (provider) => {
        setBusyProvider(provider);
        try {
            await aiAPI.reactivarProvider(provider);
            toast.success(`${provider} reactivado`);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusyProvider(null);
            cargarStatus();
        }
    };

    if (tabInvalido) {
        return <Navigate to="/404" replace />;
    }

    if (loading) {
        return <div className="card"><div className="loading">Cargando...</div></div>;
    }

    const providerDefault = local.ia_proveedor_default || 'gemini';

    // ------------------------------
    // Render helpers por tab
    // ------------------------------
    const renderGeneralTab = () => (
        <>
            <section className="config-ia-section">
                <h3 className="h3">Sistema IA</h3>
                <div className="hint" style={{ marginBottom: 12 }}>
                    Switch global. Cuando está apagado, el sistema no llama a ningún proveedor.
                </div>
                <label className="config-ia-toggle">
                    <input
                        type="checkbox"
                        checked={local.ia_activada === '1'}
                        onChange={(e) => handleLocalChange('ia_activada', e.target.checked ? '1' : '0')}
                    />
                    <span>
                        {local.ia_activada === '1'
                            ? 'IA habilitada — llamadas activas'
                            : 'IA deshabilitada — cero llamadas a proveedores'}
                    </span>
                </label>
            </section>

            <div className="divider" />

            <section className="config-ia-section">
                <h3 className="h3">Proveedor predeterminado</h3>
                <div className="hint" style={{ marginBottom: 12 }}>
                    Es el proveedor de chat que se intenta primero. Si falla con error transitorio, se prueba con el siguiente cuyo switch de fallback esté activo.
                </div>
                <div className="ia-radio-grid">
                    {PROVEEDORES_CHAT.map(p => (
                        <label
                            key={p.key}
                            className={`ia-radio-card ${providerDefault === p.key ? 'active' : ''}`}
                        >
                            <input
                                type="radio"
                                name="ia_proveedor_default"
                                value={p.key}
                                checked={providerDefault === p.key}
                                onChange={() => handleLocalChange('ia_proveedor_default', p.key)}
                            />
                            <span className="ia-radio-label">{p.label}</span>
                            {providerDefault === p.key && (
                                <span className="ia-radio-tag">Actual</span>
                            )}
                        </label>
                    ))}
                </div>
            </section>

            {aiStatus && (
                <>
                    <div className="divider" />
                    <section className="config-ia-section">
                        <div className="ia-status-header">
                            <h3 className="h3">Estado de proveedores</h3>
                            <Button
                                variant="ghost"
                                size="sm"
                                leftIcon={<RefreshCw size={13} />}
                                onClick={cargarStatus}
                            >
                                Actualizar
                            </Button>
                        </div>
                        <div className="ia-status-grid">
                            {PROVEEDORES_CHAT.map(({ key: provider, label }) => {
                                const hasKey = !!config[`${provider}_api_key`];
                                const p = aiStatus.providers?.[provider];
                                const hasStatus = !!p;
                                let cardState, badgeLabel, BadgeIcon;
                                if (!hasKey) {
                                    cardState = 'unconfigured';
                                    badgeLabel = 'No configurado';
                                    BadgeIcon = AlertTriangle;
                                } else if (!hasStatus) {
                                    cardState = 'untested';
                                    badgeLabel = 'Sin probar';
                                    BadgeIcon = AlertTriangle;
                                } else if (p.available && p.status === 'ok') {
                                    cardState = 'ok';
                                    badgeLabel = 'OK';
                                    BadgeIcon = CheckCircle2;
                                } else {
                                    cardState = 'error';
                                    badgeLabel = humanStatusLabel(p.status);
                                    BadgeIcon = AlertTriangle;
                                }
                                const isDefault = providerDefault === provider;
                                const showActions = hasKey;
                                const showReactivar = cardState === 'error';
                                return (
                                    <div key={provider} className={`ia-status-card ${cardState}`}>
                                        <div className="ia-status-top">
                                            <div className="ia-status-name">
                                                {label}
                                                {isDefault && (
                                                    <span className="ia-default-badge">Predeterminado</span>
                                                )}
                                            </div>
                                            <span className={`ia-status-badge ${cardState}`}>
                                                <BadgeIcon size={12} />
                                                {badgeLabel}
                                            </span>
                                        </div>
                                        {cardState === 'unconfigured' && (
                                            <div className="ia-status-msg">
                                                Añade la API Key en la pestaña <b>{label}</b> para habilitarlo.
                                            </div>
                                        )}
                                        {cardState === 'untested' && (
                                            <div className="ia-status-msg">
                                                Aún no se ha hecho ninguna llamada. Pulsa <b>Probar</b> en su pestaña.
                                            </div>
                                        )}
                                        {p?.message && cardState === 'error' && (
                                            <div className="ia-status-msg">{p.message}</div>
                                        )}
                                        {p && (
                                            <div className="ia-status-meta">
                                                {p.last_success_at && (
                                                    <div>
                                                        <span className="text-muted">Último éxito: </span>
                                                        {new Date(p.last_success_at).toLocaleString('es-PE', { timeZone: 'America/Lima' })}
                                                    </div>
                                                )}
                                                {p.last_error_at && (
                                                    <div>
                                                        <span className="text-muted">Último error: </span>
                                                        {new Date(p.last_error_at).toLocaleString('es-PE', { timeZone: 'America/Lima' })}
                                                    </div>
                                                )}
                                                {p.consecutive_failures > 0 && (
                                                    <div>
                                                        <span className="text-muted">Fallos consecutivos: </span>
                                                        {p.consecutive_failures}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {showActions && (
                                            <div className="ia-status-actions">
                                                <Button
                                                    variant="soft"
                                                    size="sm"
                                                    leftIcon={<Zap size={13} />}
                                                    disabled={busyProvider === provider}
                                                    onClick={() => probarProveedor(provider)}
                                                >
                                                    {busyProvider === provider ? 'Probando...' : 'Probar'}
                                                </Button>
                                                {showReactivar && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        leftIcon={<RefreshCw size={13} />}
                                                        disabled={busyProvider === provider}
                                                        onClick={() => reactivarProveedor(provider)}
                                                    >
                                                        Reactivar
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </>
            )}
        </>
    );

    // Genera el bloque de una IA de chat (Gemini/DeepSeek/Claude) con
    // API key + modelo + switch fallback + botones probar/reactivar.
    const renderChatProviderTab = ({ providerKey, label, keyPlaceholder, modelos, apiKeyClave, modeloClave, fallbackClave, keyHelpUrl, keyHelpLabel }) => {
        const isDefault = providerDefault === providerKey;
        return (
            <>
                <section className="config-ia-section">
                    <h3 className="h3">
                        {label}
                        {isDefault && <span className="ia-default-badge">Predeterminado</span>}
                    </h3>

                    <div className="field">
                        <span className="label">API Key</span>
                        <SecretInput
                            hasValue={!!config[apiKeyClave]}
                            preview={config[apiKeyClave]}
                            onSave={(val) => handleImmediateSave(apiKeyClave, val)}
                            onClear={() => handleImmediateSave(apiKeyClave, '')}
                            placeholder={keyPlaceholder}
                        />
                        {keyHelpUrl && (
                            <div className="hint">
                                Obtén tu API Key en{' '}
                                <a href={keyHelpUrl} target="_blank" rel="noreferrer" style={{ color: 'rgba(140, 220, 255, 1)' }}>
                                    {keyHelpLabel}
                                </a>.
                            </div>
                        )}
                    </div>

                    <label className="field">
                        <span className="label">Modelo</span>
                        <Select
                            styles={selectStyles}
                            options={modelos}
                            value={modelos.find(m => m.value === local[modeloClave])
                                || { value: local[modeloClave], label: `${local[modeloClave]} (desconocido — actualizar)` }}
                            onChange={(opt) => handleLocalChange(modeloClave, opt?.value)}
                            isSearchable={false}
                            menuPlacement="auto"
                        />
                    </label>

                    {!isDefault && (
                        <label className="config-ia-toggle">
                            <input
                                type="checkbox"
                                checked={local[fallbackClave] === '1'}
                                onChange={(e) => handleLocalChange(fallbackClave, e.target.checked ? '1' : '0')}
                            />
                            <span>
                                Usar {label} como fallback si {PROVEEDORES_CHAT.find(p => p.key === providerDefault)?.label} falla
                            </span>
                        </label>
                    )}

                    <div className="ia-status-actions" style={{ marginTop: 12 }}>
                        <Button
                            variant="soft"
                            size="sm"
                            leftIcon={<Zap size={13} />}
                            disabled={busyProvider === providerKey || !config[apiKeyClave]}
                            onClick={() => probarProveedor(providerKey)}
                        >
                            {busyProvider === providerKey ? 'Probando...' : 'Probar conexión'}
                        </Button>
                    </div>
                </section>
            </>
        );
    };

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="ai-title">
                    <Sparkles size={22} className="ai-icon" />
                    Proveedores de IA
                    <span className="config-ia-crumb"> / {TAB_TITLES[tab]}</span>
                </h2>
            </div>

            <div className="admin-content config-ia-content">
                {tab === 'general' && renderGeneralTab()}

                {tab === 'gemini' && renderChatProviderTab({
                    providerKey: 'gemini',
                    label: 'Gemini',
                    keyPlaceholder: 'AIza...',
                    apiKeyClave: 'gemini_api_key',
                    modeloClave: 'ia_modelo',
                    fallbackClave: 'usar_gemini_fallback',
                    keyHelpUrl: 'https://aistudio.google.com/app/apikey',
                    keyHelpLabel: 'Google AI Studio',
                    modelos: [
                        { value: 'gemini-3.8-flash',      label: 'Gemini 3.8 Flash (recomendado — flagship estable)' },
                        { value: 'gemini-3.7-flash',      label: 'Gemini 3.7 Flash (generación anterior, estable)' },
                        { value: 'gemini-3.6-flash',      label: 'Gemini 3.6 Flash (multimodal, estable)' },
                        { value: 'gemini-3.5-flash',      label: 'Gemini 3.5 Flash (heredado)' },
                        { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite (rápido y económico)' },
                        { value: 'gemini-2.5-pro',        label: 'Gemini 2.5 Pro (obsoleto — dado de baja por Google)' },
                        { value: 'gemini-2.5-flash',      label: 'Gemini 2.5 Flash (obsoleto — dado de baja por Google)' },
                        { value: 'gemini-2.0-flash',      label: 'Gemini 2.0 Flash (obsoleto — dado de baja por Google)' }
                    ]
                })}

                {tab === 'deepseek' && renderChatProviderTab({
                    providerKey: 'deepseek',
                    label: 'DeepSeek',
                    keyPlaceholder: 'sk-...',
                    apiKeyClave: 'deepseek_api_key',
                    modeloClave: 'deepseek_modelo',
                    fallbackClave: 'usar_deepseek_fallback',
                    keyHelpUrl: 'https://platform.deepseek.com/api_keys',
                    keyHelpLabel: 'platform.deepseek.com',
                    modelos: [
                        { value: 'deepseek-chat',     label: 'DeepSeek Chat (V3, rápido)' },
                        { value: 'deepseek-reasoner', label: 'DeepSeek Reasoner (R1, razonamiento)' }
                    ]
                })}

                {tab === 'claude' && renderChatProviderTab({
                    providerKey: 'claude',
                    label: 'Claude (Anthropic)',
                    keyPlaceholder: 'sk-ant-...',
                    apiKeyClave: 'claude_api_key',
                    modeloClave: 'claude_modelo',
                    fallbackClave: 'usar_claude_fallback',
                    keyHelpUrl: 'https://console.anthropic.com/settings/keys',
                    keyHelpLabel: 'console.anthropic.com',
                    modelos: [
                        { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (recomendado — barato y rápido)' },
                        { value: 'claude-sonnet-5',            label: 'Claude Sonnet 5 (más capaz, más costoso)' }
                    ]
                })}

                <div className="config-ia-footer">
                    {hayCambios && <span className="config-ia-dirty">● Cambios sin guardar</span>}
                    <button
                        className="btn neon"
                        onClick={guardar}
                        disabled={!hayCambios || saving}
                        type="button"
                    >
                        <Save size={14} />
                        {saving ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfiguracionIAPanel;
